import { useState } from 'react';
import { ScheduleAvailabilityTypes as Types } from '@frontend/api-schedule-availability';
import { DAYS_OF_WEEK } from '../constants';
import { ValidateBreakFn } from '../helpers';
import { BreakErrors, BreaksByDay } from '../types';

const addNewBreak = (breaks: string[][]) => {
  // Add a new empty break time range
  if (breaks.length === 0) {
    return [['11:00', '12:00']];
  }

  // get max end time of existing breaks
  const maxEndTime = breaks.reduce((max, current) => {
    const endTime = current[1];
    return endTime > max ? endTime : max;
  }, '12:00');

  const [startHours, startMinutes] = maxEndTime.split(':');
  const startTimeString = `${startHours}:${startMinutes}`;
  const endTimeString = `${+startHours + 1}:${startMinutes}`;

  return [...breaks, [startTimeString, endTimeString]];
};

interface UseAvailabilityHoursBreaks {
  initialBreaksByDay: BreaksByDay;
  validateAvailabilityBreak: ValidateBreakFn;
}

export const useAvailabilityHoursBreaks = ({
  initialBreaksByDay,
  validateAvailabilityBreak,
}: UseAvailabilityHoursBreaks) => {
  const [breakErrors, setBreakErrors] = useState<BreakErrors>(
    DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {} as BreakErrors)
  );
  const [breaksByDay, setBreaksByDay] = useState<BreaksByDay>(initialBreaksByDay);

  const setAvailabilityBreakErrors = (newBreaksByDay = breaksByDay) => {
    // validate break errors for each day
    const newBreakErrors: BreakErrors = DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = newBreaksByDay[day].map((breakTime, index, breaks) =>
        validateAvailabilityBreak(day, breaks, breakTime, index)
      );
      return acc;
    }, {} as BreakErrors);
    setBreakErrors(newBreakErrors);
  };

  const setAvailabilityBreaks = (newBreaksByDay: BreaksByDay) => {
    setBreaksByDay(newBreaksByDay);
    setAvailabilityBreakErrors(newBreaksByDay);
  };

  const addBreak = (day: Types.Weekday) => {
    const newBreaksByDay = { ...breaksByDay, [day]: [...addNewBreak(breaksByDay[day])] };
    setAvailabilityBreaks(newBreaksByDay);
  };

  const updateBreak = (day: Types.Weekday, index: number, newBreak: string[]) => {
    const newBreaksByDay = { ...breaksByDay };
    const newBreaks = [...newBreaksByDay[day]];
    newBreaks[index] = newBreak; // Update the break at the specified index
    newBreaksByDay[day] = newBreaks;

    setAvailabilityBreaks(newBreaksByDay);
  };

  const deleteBreak = (day: Types.Weekday, index: number) => {
    const newBreaksByDay = { ...breaksByDay };
    const newBreaks = [...newBreaksByDay[day]];
    newBreaks.splice(index, 1); // Remove the break at the specified index
    newBreaksByDay[day] = newBreaks;

    setAvailabilityBreaks(newBreaksByDay);
  };

  const clearBreaks = (day: Types.Weekday) => {
    const newBreaksByDay = { ...breaksByDay };
    newBreaksByDay[day] = []; // Clear breaks for the specified day
    setAvailabilityBreaks(newBreaksByDay);
  };

  const resetBreaksAndErrors = () => {
    setBreaksByDay(initialBreaksByDay);
    setBreakErrors(
      DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = [];
        return acc;
      }, {} as BreakErrors)
    );
  };

  return {
    breaksByDay,
    addBreak,
    updateBreak,
    deleteBreak,
    clearBreaks,
    breakErrors,
    setBreakErrors,
    resetBreaksAndErrors,
  };
};
